import { Alert, Dialog } from "@tm/components"
import { useEffect, useRef, useState } from "react"
import { useLocalization } from "@tm/localization"
import { CustomRepairTimeItemToAdd, CustomRepairTimeItemToEdit } from "@tm/data/basket"
import { useForm, FormProvider } from "react-hook-form"
import { useRepairTimesFormDefaults } from "@bundles/basket/hooks/Forms/useRepairTimesFormDefaults"
import { VatRateType } from "@tm/models"
import { zodResolver } from "@hookform/resolvers/zod"
import { EditDialogBody } from "./EditDialogBody"
import { EditDialogHeader } from "./EditDialogHeader"
import { useSchema } from "./schema"

export type DialogCustomRepairTimeAdd = CustomRepairTimeItemToAdd & { type: "add" }
export type DialogCustomRepairTimeEdit = CustomRepairTimeItemToEdit & { type: "edit" }
export type DialogCustomRepairTime = DialogCustomRepairTimeAdd | DialogCustomRepairTimeEdit

type EditDialogProps = {
    repairTime: DialogCustomRepairTime
    saveError?: string
    onClose(): void
    onSave(article: DialogCustomRepairTime, note?: string): void
}

export function EditDialog({ onClose, onSave, repairTime, saveError }: EditDialogProps) {
    const { translateText } = useLocalization()
    const [hasFixedPrice, setHasFixedPrice] = useState(repairTime.type === "edit" && !!repairTime.fixedPriceValue)
    const { vatRateItems, workCategoryItems } = useRepairTimesFormDefaults()
    const firstOpened = useRef(true)

    const schema = useSchema(hasFixedPrice)

    function createDefaultValues(): Omit<DialogCustomRepairTime, "type"> & { vatType: VatRateType } {
        return {
            ...repairTime,
            vatType: repairTime.selectedVatRate.vatType,
        }
    }

    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues: createDefaultValues(),
    })

    const { handleSubmit, formState, clearErrors, watch, setValue } = form

    const selectedCategory = watch("category")
    const workTime = watch("workTime")

    // When changing the work category, the custom Hourly Rate will be changed according to the selected category
    useEffect(() => {
        if (!firstOpened.current || !workTime) {
            setValue("customHourlyRate", workCategoryItems.find((x) => x.category === selectedCategory)?.hourlyRate)
        }
        firstOpened.current = false
    }, [selectedCategory, workCategoryItems])

    useEffect(() => {
        clearErrors()
    }, [hasFixedPrice])

    const headline = repairTime.type === "edit" ? translateText(920) : translateText(13907)

    function handleConfirmButtonClick(data: Record<string, unknown>) {
        const { vatType, ...rest } = data
        const selectedVatRate = vatRateItems.find(({ vatRate }) => vatRate.vatType === vatType)?.vatRate
        if (formState.isDirty && selectedVatRate) {
            const repairTimeToSave: DialogCustomRepairTime = {
                ...repairTime,
                selectedVatRate,
                ...rest,
            }
            onSave(repairTimeToSave)
        }
    }

    return (
        <Dialog open position="middle" maxWidth="lg">
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(handleConfirmButtonClick)}>
                    <EditDialogHeader headline={headline} onCloseButtonClick={onClose} sx={{ mb: 2 }} />
                    {saveError && <Alert size="small" severity="error" title={saveError} variant="outlined" sx={{ mb: 2 }} />}
                    <EditDialogBody hasFixedPrice={hasFixedPrice} onChangeHasFixedPrice={setHasFixedPrice} />
                </form>
            </FormProvider>
        </Dialog>
    )
}
